import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'motion/react';
import IntentSearchBar from '../search/IntentSearchBar';
import JourneyChips from './JourneyChips';
import LifecycleStagePicker from './LifecycleStagePicker';
import LifecycleStageModal from './LifecycleStageModal';
import JourneyModal from './JourneyModal';
import { getLifecycleStage } from '../../data/lifecycleStages';
import { AppStoreRating } from '../trust/AppStoreRating';
import { ease, dur } from '../../lib/motion';
import { useUserState } from '../../lib/userState';
import { JOURNEY_STORAGE_KEY, JOURNEY_DECLARED_KEY, isImprovementStage } from '../../data/improvementJourney';

// Seed the shared journey stage the instant a visitor declares it on the
// homepage, so the on-page "what's next" module and the resume prompt pick
// up the same stage everywhere they go (one continuous motion).
const seedJourneyStage = (slug) => {
  if (!isImprovementStage(slug)) return;
  try {
    window.localStorage.setItem(JOURNEY_STORAGE_KEY, slug);
    window.localStorage.setItem(JOURNEY_DECLARED_KEY, '1');
  } catch { /* ignore */ }
};

// -------------------------------------------------------------
// HERO BACKGROUND VERSION TOGGLE (2026-09-08)
// Flip USE_V3_HERO back to `false` to instantly revert to the
// previous hero images (v2 dusk build + iPad v/h + mobile). No
// other change is required - the v2 <picture> block is kept
// intact below the guard so a rollback is a one-line change.
// -------------------------------------------------------------
// 2026-06-27: V4 claymorphic neighbourhood hero (user-selected). Light
// sky art -> hero uses DARK text (navy). Flip USE_V4_HERO to false to
// instantly fall back to the V3/V2 dark-photo hero + white text.
const USE_V4_HERO = true;
const USE_V3_HERO = false;
const HERO_V3 = {
  desktop: 'https://customer-assets-rejwkqb3.emergentagent.net/job_eaad4cf4-b013-4778-bbae-d982e539f07c/artifacts/wut5whgk_desktop.png',
  ipad: 'https://customer-assets-rejwkqb3.emergentagent.net/job_eaad4cf4-b013-4778-bbae-d982e539f07c/artifacts/6xknnsd0_ipad.png',
  mobile: 'https://customer-assets-rejwkqb3.emergentagent.net/job_eaad4cf4-b013-4778-bbae-d982e539f07c/artifacts/57jqboq1_mobile.png',
};

// Per-state hero copy. `subtitleAdj` slots into
// "Research, compare and shortlist {adj} builders, designs..." so
// visitors who picked VIC read "Victorian builders" and visitors who
// picked SA read "Adelaide builders". `tryLinks` are the four inline
// example queries below the search bar - swapped per state so the
// examples point at content we actually ship for that state.
const HERO_TRY_LINKS = {
  SA: [
    { to: '/builders', label: 'compare Adelaide builders' },
    { to: '/renovations', label: 'renovating a home' },
    { to: '/swimming-pools', label: 'swimming pools' },
    { to: '/one-on-one-support', label: 'Talk to a real human' },
  ],
  VIC: [
    { to: '/home-builders-melbourne', label: 'Melbourne home builders' },
    { to: '/renovations', label: 'renovating a home' },
    { to: '/vic/grants', label: 'Victorian grants' },
    { to: '/one-on-one-support', label: 'Talk to a real human' },
  ],
};

// Neutral try-links for non-live states. Points at universal editorial
// (Building a New Home, Knockdown Rebuild, Building Glossary) and the
// national One on One Support so a NSW/QLD visitor is never sent into
// an Adelaide-suffixed URL from the hero.
const HERO_TRY_LINKS_UNIVERSAL = [
  { to: '/building-a-new-home', label: 'building a new home' },
  { to: '/renovations', label: 'renovating a home' },
  { to: '/terminology', label: 'building glossary' },
  { to: '/one-on-one-support', label: 'Talk to a real human' },
];

// -------------------------------------------------------------
// PROJECT CHIPS ROW TOGGLE (2026-06-27 · Phase 0)
// When true, the existing JourneyChips ("What are you working
// on?" - Build/Renovate/Improve/Invest/Help) lead the hero as
// the single project-first entry row, and the lifecycle stage
// picker ("Where are you in your journey?") is hidden from the
// hero (its component + content are preserved and reused in the
// Love Living band). Flip to `false` to instantly revert to the
// previous stage-picker-first hero with the "Skip - I know what
// I want" link. No other change required. Popups unchanged.
// -------------------------------------------------------------
// 2026-06-28: Flipped to `false` to trial the stage-picker-first hero
// ("Where are you in your journey?") so continuity seeding goes live and
// first-time visitors self-declare their stage. To REVERT to the
// project-chips-first hero, set this back to `true` (one-line, no other
// change needed).
const SHOW_PROJECT_CHIPS_ROW = false;

// Smart Stage Default: remember the last lifecycle stage a visitor
// opened (localStorage) and, on a FRESH session, auto-open that stage
// once (sessionStorage guard) so a returning user resumes exactly
// where they left off. Manual opens also mark the session handled so
// we never re-pop the modal during the same visit.
const LAST_STAGE_KEY = 'bp:lastLifecycleStage';
const STAGE_HANDLED_SESSION_KEY = 'bp:lifecycleStageHandled';

const rememberStage = (slug) => {
  if (!slug) return;
  try {
    window.localStorage.setItem(LAST_STAGE_KEY, slug);
    window.sessionStorage.setItem(STAGE_HANDLED_SESSION_KEY, '1');
    // Broadcast so the hero search bar can retune its placeholder
    // hints to the stage the visitor just picked (live, same session).
    window.dispatchEvent(new CustomEvent('buildpilot:stage-changed', { detail: slug }));
  } catch { /* ignore */ }
};

const HeroSection = () => {
  const reduced = useReducedMotion();
  const userState = useUserState();
  const tryLinks = HERO_TRY_LINKS[userState] || HERO_TRY_LINKS_UNIVERSAL;
  // 2026-06-25: New lifecycle-first homepage architecture.
  // - LifecycleStagePicker is the primary entry (5 stages).
  // - Each stage opens LifecycleStageModal with verified content.
  // - Clicking a project tile inside the lifecycle modal closes it
  //   and opens the canonical JourneyModal for that journey.
  // - "Skip - I know what I want" reveals the original 6-tile
  //   JourneyChips below (power-user escape hatch + one-line revert
  //   path if we decide to roll back).
  const [activeStage, setActiveStage] = useState(null);
  const [activeJourney, setActiveJourney] = useState(null);
  const [showLegacyChips, setShowLegacyChips] = useState(false);

  // Smart Stage Default — on a fresh session, auto-open the stage the
  // visitor last left off in (once). First-time visitors (no saved
  // stage) and same-session revisits (handled flag set) are skipped.
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem(STAGE_HANDLED_SESSION_KEY)) return;
      const slug = window.localStorage.getItem(LAST_STAGE_KEY);
      if (!slug) return;
      const stage = getLifecycleStage(slug);
      if (!stage) return;
      const t = setTimeout(() => {
        setActiveStage(stage);
        window.sessionStorage.setItem(STAGE_HANDLED_SESSION_KEY, '1');
      }, 900);
      return () => clearTimeout(t);
    } catch { /* ignore */ }
  }, []);
  // Restrained entrance: fade + a few-pixel upward translate. Stagger
  // is small (60ms) so the headline arrives ahead of the search/chips
  // without ever feeling sequential.
  const parent = reduced
    ? { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.2 } } }
    : { hidden: {}, visible: { transition: { delayChildren: 0.05, staggerChildren: 0.06 } } };
  const child = reduced
    ? { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.2 } } }
    : {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { duration: dur.long, ease: ease.out } },
      };
  return (
    <section className="relative min-h-screen overflow-x-clip bg-[#1d1d1f]">
      {/* Responsive hero image - WebP with JPG fallback. Four sources by device:
          desktop ≥1280, iPad landscape 1024-1279, iPad portrait 768-1023, mobile <768.
          2026-09-08: USE_V3_HERO branch loads the new dusk-house PNGs from
          customer-assets. Flip USE_V3_HERO to `false` above to revert. */}
      <div className="absolute inset-0">
        {USE_V4_HERO ? (
          <picture>
            {/* Ultra-wide / big monitors & Apple Studio Display ≥1920px -
                2:1 wide neighbourhood art so the whole diorama shows without
                cropping. (2026-06: user-supplied wide master.) */}
            <source media="(min-width: 1920px)" type="image/avif" srcSet="/images/hero-v4/desktop-wide.avif?v=12" />
            <source media="(min-width: 1920px)" type="image/webp" srcSet="/images/hero-v4/desktop-wide.webp?v=12" />
            <source media="(min-width: 1920px)" type="image/jpeg" srcSet="/images/hero-v4/desktop-wide.jpg?v=12" />
            {/* Standard desktop / laptop 1280-1919px - right-sized 1920 */}
            <source media="(min-width: 1280px)" type="image/avif" srcSet="/images/hero-v4/desktop-1920.avif?v=8" />
            <source media="(min-width: 1280px)" type="image/webp" srcSet="/images/hero-v4/desktop-1920.webp?v=8" />
            <source media="(min-width: 1280px)" type="image/jpeg" srcSet="/images/hero-v4/desktop.jpg?v=8" />
            {/* iPad landscape */}
            <source media="(min-width: 1024px) and (orientation: landscape)" type="image/avif" srcSet="/images/hero-v4/ipad-h.avif?v=8" />
            <source media="(min-width: 1024px) and (orientation: landscape)" type="image/webp" srcSet="/images/hero-v4/ipad-h.webp?v=8" />
            <source media="(min-width: 1024px) and (orientation: landscape)" type="image/jpeg" srcSet="/images/hero-v4/ipad-h.jpg?v=8" />
            {/* iPad portrait */}
            <source media="(min-width: 768px) and (orientation: portrait)" type="image/avif" srcSet="/images/hero-v4/ipad-v.avif?v=8" />
            <source media="(min-width: 768px) and (orientation: portrait)" type="image/webp" srcSet="/images/hero-v4/ipad-v.webp?v=8" />
            <source media="(min-width: 768px) and (orientation: portrait)" type="image/jpeg" srcSet="/images/hero-v4/ipad-v.jpg?v=8" />
            {/* Tablet fallbacks */}
            <source media="(min-width: 1024px)" type="image/avif" srcSet="/images/hero-v4/ipad-h.avif?v=8" />
            <source media="(min-width: 1024px)" type="image/webp" srcSet="/images/hero-v4/ipad-h.webp?v=8" />
            <source media="(min-width: 768px)" type="image/avif" srcSet="/images/hero-v4/ipad-v.avif?v=8" />
            <source media="(min-width: 768px)" type="image/webp" srcSet="/images/hero-v4/ipad-v.webp?v=8" />
            {/* Mobile <768px */}
            <source type="image/avif" srcSet="/images/hero-v4/mobile.avif?v=8" />
            <source type="image/webp" srcSet="/images/hero-v4/mobile.webp?v=8" />
            <img
              src="/images/hero-v4/mobile.jpg?v=8"
              alt="A claymorphic illustration of an Australian neighbourhood being built - homes, a crane and a timber-framed house by the coast"
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover object-bottom xl:object-[center_28%]"
              data-testid="hero-bg-v4"
            />
          </picture>
        ) : USE_V3_HERO ? (
          <picture>
            {/* Desktop ≥1280px */}
            <source media="(min-width: 1280px)" srcSet={HERO_V3.desktop} />
            {/* iPad (768-1279px, any orientation) - single asset covers both */}
            <source media="(min-width: 768px)" srcSet={HERO_V3.ipad} />
            {/* Mobile <768px */}
            <img
              src={HERO_V3.mobile}
              alt="A dusk render of a new build home at twilight - sunset sky over a single-storey Australian home"
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="hero-bg-v3"
            />
          </picture>
        ) : (
          <picture>
            {/* Desktop ≥1280px */}
            <source media="(min-width: 1280px)" type="image/webp" srcSet="/images/hero-dusk-build-v2.webp?v=2026022302" />
            <source media="(min-width: 1280px)" type="image/jpeg" srcSet="/images/hero-dusk-build-v2.jpg?v=2026022302" />
            {/* iPad landscape (1024-1279px, or any landscape orientation in iPad range) */}
            <source media="(min-width: 1024px) and (orientation: landscape)" type="image/webp" srcSet="/images/hero/hero-ipad-h.webp?v=2026061903" />
            <source media="(min-width: 1024px) and (orientation: landscape)" type="image/jpeg" srcSet="/images/hero/hero-ipad-h.jpg?v=2026061903" />
            {/* iPad portrait (768-1023px portrait, or any portrait in iPad range) */}
            <source media="(min-width: 768px) and (orientation: portrait)" type="image/webp" srcSet="/images/hero/hero-ipad-v.webp?v=2026061903" />
            <source media="(min-width: 768px) and (orientation: portrait)" type="image/jpeg" srcSet="/images/hero/hero-ipad-v.jpg?v=2026061903" />
            {/* Tablet landscape fallback for 1024-1279px (covers split-screen iPads) */}
            <source media="(min-width: 1024px)" type="image/webp" srcSet="/images/hero/hero-ipad-h.webp?v=2026061903" />
            <source media="(min-width: 1024px)" type="image/jpeg" srcSet="/images/hero/hero-ipad-h.jpg?v=2026061903" />
            {/* Tablet portrait fallback */}
            <source media="(min-width: 768px)" type="image/webp" srcSet="/images/hero/hero-ipad-v.webp?v=2026061903" />
            <source media="(min-width: 768px)" type="image/jpeg" srcSet="/images/hero/hero-ipad-v.jpg?v=2026061903" />
            {/* Mobile <768px */}
            <source type="image/webp" srcSet="/images/hero/hero-mobile.webp?v=2026061903" />
            <img
              src="/images/hero/hero-mobile.jpg?v=2026061903"
              alt="Adelaide families walking through a new home estate at sunset"
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </picture>
        )}
        {/* Overlays: v3 images already carry their own bottom fade-to-white
            and a dark navy sky, so we skip the heavy 50% black wash + the
            160-240px bottom gradient (they were doubling up). Instead we
            add a light top-only vignette purely for headline legibility,
            plus a small, soft bottom fade to blend the image seam into
            the white section below (much lighter than the v2 stack). */}
        {USE_V4_HERO ? (
          <>
            {/* V4: dark top overlay so the white header, mega menu and
                headline stay legible over the sky; fades out by mid-hero
                so the diorama below stays vivid. Plus a bottom fade into
                the white sections below. */}
            <div
              className="absolute inset-x-0 top-0 h-[60%] pointer-events-none"
              style={{
                background: 'linear-gradient(to bottom, rgba(6,16,34,0.74) 0%, rgba(6,16,34,0.52) 38%, rgba(6,16,34,0.24) 72%, rgba(6,16,34,0) 100%)',
              }}
              data-testid="hero-top-scrim-v4"
            />
            <div
              className="absolute bottom-0 left-0 right-0 h-[70px] sm:h-[120px] lg:h-[150px] pointer-events-none"
              style={{
                background: 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0.7) 40%, rgba(255,255,255,0) 100%)',
              }}
              data-testid="hero-bottom-blend-v4"
            />
          </>
        ) : USE_V3_HERO ? (
          <>
            {/* Light full-screen wash - 20% (down from v2's 50%) */}
            <div className="absolute inset-0 bg-black/20" data-testid="hero-wash-v3" />
            <div
              className="absolute inset-x-0 top-0 h-[55%] pointer-events-none"
              style={{
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0) 100%)',
              }}
              data-testid="hero-top-vignette"
            />
            <div
              className="absolute bottom-0 left-0 right-0 h-[60px] sm:h-[70px] lg:h-[80px] pointer-events-none"
              style={{
                background: 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0.6) 45%, rgba(255,255,255,0) 100%)',
              }}
              data-testid="hero-bottom-blend"
            />
          </>
        ) : (
          <>
            {/* Dark overlay */}
            <div className="absolute inset-0 bg-black/50" />
            {/* Bottom fade to white - kept short on mobile so the dark
                image extends behind the chip row (chips read against the
                image, not the white fade). Taller on tablet/desktop where
                the hero has far more vertical room. */}
            <div className="absolute bottom-0 left-0 right-0 h-[88px] sm:h-[200px] lg:h-[240px]" style={{
              background: 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 25%, rgba(255,255,255,0.55) 55%, rgba(255,255,255,0.15) 85%, rgba(255,255,255,0) 100%)'
            }} />
          </>
        )}
      </div>

      {/* Content - Positioned higher on mobile, centered on desktop.
          2026-02-19: Tightened mobile vertical rhythm. Mobile was
          running 100vh + heavy paddings; this brings the hero down
          to ~90vh with reduced top/bottom and tighter inter-element
          margins. Desktop only loses ~5vh - barely noticeable, but
          the search bar now sits closer to the fold. */}
      <div className="relative z-10 min-h-[78vh] sm:min-h-[108vh] lg:min-h-[112vh] 2xl:min-h-[92vh] flex items-start sm:items-center justify-center pt-[calc(var(--bp-hero-top)+16px)] sm:pt-32 lg:pt-[calc(var(--bp-hero-top)+12px)] pb-10 sm:pb-64 lg:pb-72 2xl:pb-48">
        <motion.div
          className="w-full max-w-[1100px] 2xl:max-w-[1360px] mx-auto px-6 sm:px-6 lg:px-8 text-center mt-2 sm:mt-0"
          variants={parent}
          initial="hidden"
          animate="visible"
        >
          {/* Main Headline - core brand line: Navigate. Shortlist. Build with Confidence.
              2026-02-19: Mobile H1 sits at 32px so it's reliably larger
              than every section H2 below (the "only build companion" H2
              is 28px). H1 must always be the biggest type on the page.
              2026-07-17: added a 2xl step (>=1536px) so the hero scales
              up on 5K Studio Display and other very wide monitors without
              affecting the laptop experience under 1536px. */}
          <motion.h1
            variants={child}
            className="text-[40px] sm:text-[44px] lg:text-[54px] 2xl:text-[72px] font-bold leading-[1.08] sm:leading-[1.08] tracking-[-0.025em] mb-3 sm:mb-6 text-center max-w-[15ch] sm:max-w-[20ch] 2xl:max-w-[24ch] mx-auto text-white"
            style={{ filter: 'drop-shadow(0 2px 20px rgba(0, 0, 0, 0.35))' }}
            data-testid="hero-h1"
          >
            Australia's home <span className="whitespace-nowrap">for <span className="gradient-text-orange">building</span></span>
          </motion.h1>

          {/* Supporting Line - home-project positioning. Universal copy;
              local/state flavour now lives in the try-links below. */}
          <motion.p variants={child} className="text-[14.5px] sm:text-[19px] lg:text-[21px] 2xl:text-[24px] text-white/80 mb-4 sm:mb-10 max-w-[420px] sm:max-w-[680px] 2xl:max-w-[820px] mx-auto leading-[1.5] sm:leading-[1.45] font-normal" data-testid="hero-subtitle" style={{ textShadow: '0 1px 16px rgba(0,0,0,0.45)' }}>
            Build, renovate or improve with the right people, products, information and tools behind you.
          </motion.p>

          {/* Intent search bar - rotating placeholder hints (no explicit placeholder = rotating mode) */}
          <motion.div variants={child} className="mb-4 sm:mb-10" data-testid="hero-search-wrapper">
            <IntentSearchBar variant="hero" />
            <p className="mt-3 sm:mt-4 text-[12px] sm:text-[14px] text-white/75 leading-[1.5]" data-testid="hero-try-links" style={{ textShadow: '0 1px 12px rgba(0,0,0,0.4)' }}>
              Try{' '}
              {tryLinks.map((l, i) => (
                <React.Fragment key={l.to}>
                  <Link to={l.to} className="text-white underline-offset-2 hover:underline">{l.label}</Link>
                  {i < tryLinks.length - 2 ? ', ' : i === tryLinks.length - 2 ? ', or ' : '.'}
                </React.Fragment>
              ))}
            </p>
            {/* Live App Store rating - official trust signal beside the
                primary search/CTA. Hidden automatically if the live
                rating can't be fetched (never shows a fabricated number). */}
            <div className="mt-3 sm:mt-4 flex justify-center text-[12px] sm:text-[14px]" data-testid="hero-appstore-rating">
              <AppStoreRating variant="inline" className="text-white/75" />
            </div>
          </motion.div>

          {/* Project chips row - "What are you working on?" - the
              breadth signal so a renovator / pool / landscaping
              visitor sees themselves immediately. Reuses JourneyChips
              and its popups EXACTLY (all internal references intact).
              Gated by SHOW_PROJECT_CHIPS_ROW for a one-line revert. */}
          {SHOW_PROJECT_CHIPS_ROW && (
            <motion.div variants={child} className="mb-4 sm:mb-7" data-testid="hero-project-chips">
              <p className="text-[11px] sm:text-[12px] font-semibold uppercase tracking-[0.04em] mb-2.5 sm:mb-3 text-white/70">
                What are you working on?
              </p>
              <JourneyChips tone="light" />
            </motion.div>
          )}

          {/* Lifecycle stage picker - hidden from the hero while the
              project-chips row leads (SHOW_PROJECT_CHIPS_ROW). The
              component + lifecycleStages content are preserved and
              reused in the Love Living band + future "what's next"
              layer. Flip the toggle to false to restore the
              stage-picker-first hero (with its "Skip" escape hatch). */}
          {!SHOW_PROJECT_CHIPS_ROW && (
            <motion.div variants={child}>
              <LifecycleStagePicker
                tone="light"
                onSelectStage={(stage) => { seedJourneyStage(stage?.slug); rememberStage(stage?.slug); setActiveStage(stage); }}
                showSkip={!showLegacyChips}
                onSkip={() => setShowLegacyChips(true)}
              />
            </motion.div>
          )}

          {/* Legacy 6-chip picker - shown only when "Skip" is clicked
              (only reachable when SHOW_PROJECT_CHIPS_ROW is false).
              Kept intact for parity + one-line revert path. */}
          {showLegacyChips && (
            <motion.div
              variants={child}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="mt-5"
            >
              <JourneyChips tone="light" />
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Lifecycle modal - rendered into portal */}
      {activeStage && (
        <LifecycleStageModal
          stage={activeStage}
          onClose={() => setActiveStage(null)}
          onSelectJourney={(j) => setActiveJourney(j)}
        />
      )}

      {/* Canonical JourneyModal - opens when a project tile inside
          the lifecycle modal is clicked. Same component the existing
          JourneyChips uses, so downstream behaviour is unchanged. */}
      {activeJourney && (
        <JourneyModal journey={activeJourney} onClose={() => setActiveJourney(null)} />
      )}
    </section>
  );
};

export default HeroSection;
